import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, useBlocker, useBeforeUnload } from 'react-router-dom';
import { Loading } from '../components/Loading';
import { CandidatesTable } from '../components/dashboard';
import { CandidateSidebar } from '../components/dashboard';
import { CreateJobModal } from '../components/dashboard/create-job-modal';
import { EditJobDetailsModal } from '../components/dashboard/edit-job-modal/EditJobDetailsModal';
import { UploadedCvsModal } from '../components/dashboard';
import { SourcingTab } from '../components/dashboard/SourcingTab';
import { StageManagementTab } from '../components/dashboard/stage-management';
import { JobStatsPanel } from '../components/dashboard/JobStatsPanel';
import { CandidatesDashboard } from '../components/dashboard/CandidatesDashboard';
import { CandidateKanban } from '../components/dashboard/candidates-kanban';
import { CandidatesToolbar } from '../components/dashboard/candidates-toolbar';
import {
  PERSISTED_CANDIDATES_VIEWS,
  type CandidatesPipelineView,
} from '../components/dashboard/CandidatesViewSwitcher';
import { AddCvModal } from '../components/dashboard';
// import { ExamConfigModal } from '../components/dashboard'; // Replaced with inline editor
import { ExamConfigEditor, type ExamConfigEditorHandle } from '../components/exam-config/ExamConfigEditor';
import { BrowseResumeBankModal } from '../components/dashboard';
import { useCandidateAssessmentScores } from '../components/dashboard/candidates-table/useCandidateAssessmentScores';
import {
  applyActionFeedback,
  feedbackErrorMessage,
  feedbackForSendInterview,
  isInterviewInvitationAlreadySent,
} from '../utils/pipeline-action-feedback';
import {
  useJobOffer,
  useDeleteJobOffer,
  useArchiveJobOffer,
  useJobCandidates,
  useJobPipeline,
  useInvalidateJobQueries,
  useSelectCandidatesForInterview,
  useUpdateJobOffer,
} from "../hooks/useJobOffers";
import {
  createStageResolver,
  findStageByType,
  sortStagesByPosition,
  stageWillNotify,
} from "../utils/pipeline-stages";
import { JobStatus } from '../types/job.types';
import { ExamConfig } from '../types/job.types';
import {
  JobOffer,
  MatchingPriorities,
  DEFAULT_MATCHING_PRIORITIES,
} from '../models/job';
import { Candidate } from '../models/job';
import {
  Send,
  Trash2,
  MoreVertical,
  FileText,
  Archive,
  Download,
  AlertCircle,
  ChevronLeft,
  FilePlus2,
  Briefcase,
  Users,
  Settings,
  Layers,
  MapPin,
  Building2,
  GraduationCap,
  Clock,
  Calendar,
  Wifi,
  ScrollText,
  Pencil,
  FileDown,
} from 'lucide-react';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { useJobDownload } from '../hooks/useJobDownload';
import { SendInterviewConfirmationDialog } from '../components/dashboard';
import { ManatalBadge } from '../components/dashboard/ManatalBadge';
import { Button } from '@/components/ui/atoms/Button';
import { formatDate } from '@/utils/date';
import { DropdownMenu } from '@/components/ui/organisms/DropdownMenu';
import { useGuardedAction } from '../hooks/useGuardedAction';
import { useUserJobSessionPreference } from '../hooks/useUserJobSessionPreference';
import { getMetaCvNavigationCandidates } from '../utils/candidate-navigation';
import { useDemoMode } from '../components/providers/DemoModeContext';
import { AddFromPoolModal } from '@/components/ui/organisms/AddFromPoolModal';
import { SourceWithAgentSheet } from '@/components/ui/organisms/SourceWithAgentSheet';
import { Tag } from '@/components/ui/atoms/Tag';
import { Tabs } from '@/components/ui/atoms/Tabs';
import { MatchingPrioritySelector } from '@/components/dashboard/matching-weights/MatchingPrioritySelector';
import {
  hasReadyUploadedJobDescription,
  resolveJobOfferLastUpdatedDate,
} from '@/utils/job-details-downloads';
import { IconWrapper } from '@/components/ui/atoms/IconWrapper';

/** Title + supporting line; description is visual-only (aria-label carries full name). */
function DownloadMenuItemLabel({ title, description }: { title: string; description: string }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left whitespace-normal">
      <span className="text-sm font-medium leading-snug text-dark-invirtus">{title}</span>
      <span className="text-xs font-body leading-snug text-dark-invirtus/55" aria-hidden="true">
        {description}
      </span>
    </span>
  );
}

interface JobDownloadMenuItemsProps {
  onDownloadPdf: () => void;
  onDownloadDescription: () => void;
  hasUploadedJobDescription: boolean;
  pdfLastUpdatedHint: string | null;
  pdfTitle: string;
  pdfAriaLabel: string;
  descriptionTitle: string;
  descriptionHint: string;
  descriptionAriaLabel: string;
}

/** Shared PDF + uploaded-JD actions for header and overview download menus. */
function JobDownloadMenuItems({
  onDownloadPdf,
  onDownloadDescription,
  hasUploadedJobDescription,
  pdfLastUpdatedHint,
  pdfTitle,
  pdfAriaLabel,
  descriptionTitle,
  descriptionHint,
  descriptionAriaLabel,
}: JobDownloadMenuItemsProps) {
  const pdfAccessibleName = pdfLastUpdatedHint
    ? `${pdfAriaLabel}. ${pdfLastUpdatedHint}`
    : pdfAriaLabel;

  return (
    <>
      <DropdownMenu.Item
        onClick={onDownloadPdf}
        className="!items-start whitespace-normal"
        aria-label={pdfAccessibleName}
      >
        <IconWrapper size="sm" color="secondary">
          <FileDown  className="mt-0.5" />
        </IconWrapper>
        {pdfLastUpdatedHint ? (
          <DownloadMenuItemLabel title={pdfTitle} description={pdfLastUpdatedHint} />
        ) : (
          <span>{pdfTitle}</span>
        )}
      </DropdownMenu.Item>
      {hasUploadedJobDescription && (
        <DropdownMenu.Item
          onClick={onDownloadDescription}
          className="!items-start whitespace-normal"
          aria-label={`${descriptionAriaLabel}. ${descriptionHint}`}
        >
          <IconWrapper size="sm" color="secondary">
            <Download className="mt-0.5" />
          </IconWrapper>
          <DownloadMenuItemLabel title={descriptionTitle} description={descriptionHint} />
        </DropdownMenu.Item>
      )}
    </>
  );
}

type OverviewCardProps = { title: string; action?: React.ReactNode; children: React.ReactNode };
const OverviewCard = ({ title, action, children }: OverviewCardProps) => (
  <section className="rounded-xl border border-light-lila/40 bg-light overflow-hidden">
    <header className="h-11 px-5 border-b border-light-lila/30 flex items-center justify-between gap-2">
      <h6 className="text-xs font-semibold uppercase tracking-[0.07em] text-dark-invirtus/55 font-body">
        {title}
      </h6>
      {action}
    </header>
    <div className="p-5">{children}</div>
  </section>
);

type OverviewNormSkill = { name: string; mustHave: boolean };
type OverviewSkillGroupProps = { label: string; skills: OverviewNormSkill[]; pillClass: string };
const OverviewSkillGroup = ({ label, skills, pillClass }: OverviewSkillGroupProps) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-dark-invirtus/45 font-body mb-2.5">
      {label}
    </div>
    <div className="flex flex-wrap gap-2">
      {skills.map((s, i) => (
        <span
          key={`${label}-${i}-${s.name}`}
          className={`inline-flex items-center h-7 px-3 rounded-full text-xs font-medium font-body ${pillClass}`}
        >
          {s.name}
        </span>
      ))}
    </div>
  </div>
);

export const JobDetailsPage = () => {
  const { t } = useTranslation(['job', 'common', 'talentPool', 'agents']);
  const { isDemoMode, mockJob, mockCandidates } = useDemoMode();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { isLoading: isAuthLoading } = useAuth0();

  // Active tab — synced with the ?tab= query param for deep-linking & back-button.
  const [searchParams, setSearchParams] = useSearchParams();
  type JobDetailTab = 'overview' | 'candidates' | 'exam' | 'sourcing' | 'stages';
  const TAB_IDS: JobDetailTab[] = ['overview', 'candidates', 'exam', 'sourcing', 'stages'];
  const urlTab = searchParams.get('tab') as JobDetailTab | null;
  const activeTab: JobDetailTab = urlTab && TAB_IDS.includes(urlTab) ? urlTab : 'candidates';
  const setActiveTab = (tab: JobDetailTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'candidates') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: false });
  };
  const [examDirty, setExamDirty] = useState(false);
  const examEditorRef = useRef<ExamConfigEditorHandle>(null);

  // Blocks all navigations (tab switches via ?tab=, sidebar, browser back/forward)
  // while the exam tab has unsaved changes. Tab switches go through setSearchParams
  // so the route blocker catches them too — no separate pendingTab needed.
  const routeBlocker = useBlocker(examDirty && activeTab === 'exam');

  // Also block browser-level unload (tab close, hard refresh, address bar).
  useBeforeUnload(
    useCallback(
      (e) => { if (examDirty && activeTab === 'exam') e.preventDefault(); },
      [examDirty, activeTab],
    ),
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'delete' | 'archive' | null>(null);
  const [selectedJobForAction, setSelectedJobForAction] = useState<JobOffer | null>(null);
  const [dialogOrigin, setDialogOrigin] = useState<string | undefined>(undefined);
  const [showSendInterviewDialog, setShowSendInterviewDialog] = useState(false);
  const [showStepperModal, setShowStepperModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [stepperDraft, setStepperDraft] = useState<{ file: File | null; description: string }>({
    file: null,
    description: '',
  });
  const [isRefineDismissed, setIsRefineDismissed] = useState(false);
  const [showAddCvModal, setShowAddCvModal] = useState(false);
  // const [showExamChooserModal, setShowExamChooserModal] = useState(false);
  const [showUploadedCvsModal, setShowUploadedCvsModal] = useState(false);
  const [showBrowseResumeBankModal, setShowBrowseResumeBankModal] = useState(false);
  // Add-from-pool panel (talent pool feature, plan 01-04)
  const [showAddFromPoolModal, setShowAddFromPoolModal] = useState(false);
  const [showSourceAgentModal, setShowSourceAgentModal] = useState(false);
  const [examConfig] = useState<ExamConfig>({
    technical: {
      coreConceptsStrengths: true,
      coreConceptsWeaknesses: true,
      algorithmic: true,
      cleanCode: true,
      debugging: true,
      tradeoffs: true,
      ai_tools: true,
    },
    softSkills: {
      communication: true,
      teamSpirit: true,
      selfAwareness: true,
      growthMindset: true,
      adaptability: true,
      caseStudy: false,
      lastExperience: true,
    },
  });

  // Ref for auto-dismissing processing toast
  const processingToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cvPollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // State management
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>();
  const [isCandidateSidebarOpen, setIsCandidateSidebarOpen] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [preselectCandidateId, setPreselectCandidateId] = useState<string>();
  const { value: savedCandidatesView, setValue: setSavedCandidatesView } =
    useUserJobSessionPreference('candidates-view', jobId ?? '', PERSISTED_CANDIDATES_VIEWS, 'table');
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const candidatesView: CandidatesPipelineView = isDashboardOpen ? 'dashboard' : savedCandidatesView;
  // Mobile tab state retained for legacy effects below; the new ?tab= URL param drives the visible tabs.
  const [activeMobileTab, setActiveMobileTab] = useState<'job-details' | 'candidates' | 'exam-config'>('candidates');
  void activeMobileTab;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [visibleCandidates, setVisibleCandidates] = useState<Candidate[]>([]);
  /** Kanban column the Meta CV was opened from; null = Table / deep link. */
  const [metaCvColumnCandidateIds, setMetaCvColumnCandidateIds] = useState<string[] | null>(null);

  const isDemoJobRoute = Boolean(
    isDemoMode && mockJob && mockCandidates && jobId === mockJob.id
  );

  const demoMobileJobTabPrimedRef = useRef(false);
  useEffect(() => {
    if (!isDemoJobRoute || demoMobileJobTabPrimedRef.current) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    setActiveMobileTab('job-details');
    demoMobileJobTabPrimedRef.current = true;
  }, [isDemoJobRoute]);

  // Use the hook to fetch job data with automatic refetchInterval based on status
  const { data: jobData, isLoading: isJobLoading, error } = useJobOffer(jobId || '', {
    enabled: !isDemoJobRoute,
  });

  // Fetch candidates for the job (skip network in demo — mock list is instant)
  const { data: candidatesData, isLoading: isCandidatesLoadingFromApi } = useJobCandidates(jobId || '', {
    enabled: !isDemoJobRoute,
  });
  const job = isDemoJobRoute ? mockJob : jobData;
  const candidates = isDemoJobRoute ? mockCandidates : candidatesData;
  const isCandidatesLoading = isDemoJobRoute ? false : isCandidatesLoadingFromApi;

  // Pipeline stages drive the Table's tabs (same source and cache key as the Kanban).
  const { data: pipeline } = useJobPipeline(jobId || '', {
    enabled: !isDemoJobRoute,
  });
  const stages = useMemo(() => sortStagesByPosition(pipeline?.stages), [pipeline]);
  // Until pipeline loads, keep historical "will notify" copy (enabled default).
  const interviewingWillNotify = useMemo(() => {
    if (!pipeline?.stages) return true;
    return stageWillNotify(findStageByType(pipeline.stages, 'interviewing'));
  }, [pipeline?.stages]);
  // Falls back to the first stage when nothing is selected yet, or when the
  // selected stage was deleted from the Kanban.
  const activeStageId =
    stages.find((stage) => stage.id === selectedStageId)?.id ??
    stages[0]?.id ??
    '';

  const {
    fitScores,
    techScores,
    softSkillsScores,
    cheatingFlags,
  } = useCandidateAssessmentScores(candidates || []);

  const deleteJobMutation = useDeleteJobOffer();
  const archiveJobMutation = useArchiveJobOffer();
  const selectCandidatesMutation = useSelectCandidatesForInterview(jobId || "");
  const updateJobOfferMutation = useUpdateJobOffer(jobId || "");
  const [matchingPriorities, setMatchingPriorities] = useState<MatchingPriorities>(DEFAULT_MATCHING_PRIORITIES);
  const [savedMatchingPriorities, setSavedMatchingPriorities] = useState<MatchingPriorities>(DEFAULT_MATCHING_PRIORITIES);
  const { guardedAction: guardCvAction } = useGuardedAction('cv', 1, 'add_cv_button');
  const { guardedAction: guardInterviewAction } = useGuardedAction('interview', () => selectedCandidates.length, 'send_interview_button');
  // Get invalidate job queries function
  const invalidateJobQueries = useInvalidateJobQueries();

  // Download functionality
  const { downloadJobDescriptionForRecruiter, downloadJobOfferForCandidate } = useJobDownload();

  const isLoading = !isDemoMode && (isAuthLoading || isJobLoading);

  useEffect(() => {
    if (!job) return;
    const initialPriorities = job.matchingPriorities ?? DEFAULT_MATCHING_PRIORITIES;
    setMatchingPriorities(initialPriorities);
    setSavedMatchingPriorities(initialPriorities);
  }, [job]);

  const hasWeightChanges = useMemo(
    () => (
      matchingPriorities.yoe !== savedMatchingPriorities.yoe
      || matchingPriorities.skills_match !== savedMatchingPriorities.skills_match
      || matchingPriorities.degree !== savedMatchingPriorities.degree
      || matchingPriorities.sector_experience !== savedMatchingPriorities.sector_experience
    ),
    [matchingPriorities, savedMatchingPriorities],
  );

  // Cleanup on unmount - clear any pending timeouts
  useEffect(() => {
    return () => {
      if (processingToastTimeoutRef.current) {
        clearTimeout(processingToastTimeoutRef.current);
        processingToastTimeoutRef.current = null;
      }
      if (cvPollIntervalRef.current) {
        clearInterval(cvPollIntervalRef.current);
        cvPollIntervalRef.current = null;
      }
    };
  }, []);

  const dismissedStorageKey = jobId ? `job-refine-dismissed-${jobId}` : '';

  useEffect(() => {
    if (!dismissedStorageKey || typeof window === 'undefined') {
      setIsRefineDismissed(false);
      return;
    }
    setIsRefineDismissed(window.sessionStorage.getItem(dismissedStorageKey) === '1');
  }, [dismissedStorageKey]);

  useEffect(() => {
    if (!job || !jobId) return;

    const requiresReview = Boolean(job.requiresReview);
    const needsRefineStep = requiresReview && (job.status === JobStatus.ACTIVE || job.status === JobStatus.INTERVIEWING);

    if (!needsRefineStep) {
      if (dismissedStorageKey && typeof window !== 'undefined') {
        window.sessionStorage.removeItem(dismissedStorageKey);
      }
      setIsRefineDismissed(false);
      return;
    }

    if (!isRefineDismissed) {
      setStepperDraft((previous) => {
        if (previous.file || previous.description.trim().length > 0) {
          return previous;
        }
        return {
          file: null,
          description: job.customInstructions ?? job.description ?? '',
        };
      });
      setShowStepperModal(true);
    }
  }, [job, jobId, isRefineDismissed, dismissedStorageKey]);



  // CV Upload handlers
  const handleOpenAddCvModal = useCallback(
    guardCvAction(() => {
      setShowAddCvModal(true);
    }),
    [guardCvAction]
  );

  const handleCloseAddCvModal = useCallback(() => {
    setShowAddCvModal(false);
  }, []);

  const handleCvUploadComplete = useCallback((success: boolean = true) => {
    if (!success) {
      toast.error(t('job:toasts.cv_upload_failed'));
      return;
    }

    if (!jobId) return;

    const toastId = toast.info(t('job:toasts.cv_processing'), {
      duration: 20000, // Auto-dismiss after 20 seconds
      className: 'invirtus-toast-long', // 20s progress bar to match the duration
    });

    // Clear any existing timeout
    if (processingToastTimeoutRef.current) {
      clearTimeout(processingToastTimeoutRef.current);
    }

    // Set timeout to auto-dismiss the processing toast
    processingToastTimeoutRef.current = setTimeout(() => {
      toast.dismiss(toastId);
      processingToastTimeoutRef.current = null;
    }, 20000); // 20 seconds

    // Poll the candidate list across the processing window so newly processed
    // CVs appear without a manual reload — SSE invalidation is best-effort.
    if (cvPollIntervalRef.current) clearInterval(cvPollIntervalRef.current);
    invalidateJobQueries(jobId, { shouldPoll: false });
    let polls = 0;
    cvPollIntervalRef.current = setInterval(() => {
      polls += 1;
      invalidateJobQueries(jobId, { shouldPoll: false });
      if (polls >= 10 && cvPollIntervalRef.current) {
        clearInterval(cvPollIntervalRef.current);
        cvPollIntervalRef.current = null;
      }
    }, 4000); // ~40s of polling
  }, [jobId, invalidateJobQueries]);

  // Exam Chooser handlers - now redirects to full page
  // const handleOpenExamChooserModal = useCallback(() => {
  //   setShowExamChooserModal(true);
  // }, []);

  // const handleCloseExamChooserModal = useCallback(() => {
  //   setShowExamChooserModal(false);
  // }, []);

  const handleCloseUploadedCvsModal = useCallback(() => {
    setShowUploadedCvsModal(false);
  }, []);

  // const handleOpenBrowseResumeBankModal = useCallback(() => {
  //   setShowBrowseResumeBankModal(true);
  // }, []);

  const handleCloseBrowseResumeBankModal = useCallback(() => {
    setShowBrowseResumeBankModal(false);
  }, []);

  const handleBrowseResumeBankSuccess = useCallback(() => {
    setShowBrowseResumeBankModal(false);
    if (jobId) {
      invalidateJobQueries(jobId);
    }
  }, [jobId, invalidateJobQueries]);

  // Exam config change handler - no longer needed with full-page route
  // const handleExamConfigChange = useCallback((config: ExamConfig) => {
  //   setExamConfig(config);
  //   toast.success("Exam configuration updated successfully!");
  // }, []);

  // Candidate handlers
  const handleCandidatesSelected = useCallback(() => {
    if (jobId) {
      invalidateJobQueries(jobId);
    }
  }, [jobId, invalidateJobQueries]);

  const handleSelectedCandidatesChange = useCallback((selectedIds: string[]) => {
    setSelectedCandidates(selectedIds);
  }, []);

  const handleCandidateClick = useCallback(
    (candidate: Candidate, columnCandidateIds?: string[]) => {
      setSelectedCandidateId(candidate.id);
      setMetaCvColumnCandidateIds(columnCandidateIds ?? null);
      setIsCandidateSidebarOpen(true);
    },
    []
  );

  const metaCvNavigationCandidates = useMemo(
    () =>
      getMetaCvNavigationCandidates(
        metaCvColumnCandidateIds,
        candidates ?? [],
        visibleCandidates
      ),
    [metaCvColumnCandidateIds, candidates, visibleCandidates]
  );

  // Deep link: ?candidate=<id> opens that candidate's panel once loaded
  // (e.g. "Review" from the Home page interviews feed). Page-independent —
  // the candidate is resolved from the full list, not the visible table page.
  const candidateParam = searchParams.get('candidate');
  useEffect(() => {
    if (!candidateParam || !candidates) return;
    const found = candidates.find((c) => c.id === candidateParam);
    if (!found) return;
    setSelectedCandidateId(found.id);
    setMetaCvColumnCandidateIds(null);
    setIsCandidateSidebarOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('candidate');
    setSearchParams(next, { replace: true });
  }, [candidateParam, candidates, searchParams, setSearchParams]);

  // Deep link: ?invite=<id> lands on the candidate's stage, selects the
  // candidate (ready to send an interview) — used by Home's "Invite" action.
  const inviteParam = searchParams.get('invite');
  useEffect(() => {
    if (!inviteParam || !candidates) return;
    const found = candidates.find((c) => c.id === inviteParam);
    if (!found) return;
    setSelectedStageId(createStageResolver(stages)(found));
    setPreselectCandidateId(found.id);
    const next = new URLSearchParams(searchParams);
    next.delete('invite');
    setSearchParams(next, { replace: true });
  }, [inviteParam, candidates, stages, searchParams, setSearchParams]);

  // Refresh candidates list (manual refresh uses the same pipeline as status-changing mutations)
  const handleRefreshCandidates = useCallback(() => {
    setIsRefreshing(true);
    handleCandidatesSelected();
  }, [handleCandidatesSelected]);

  // Send interview handler - shows confirmation dialog
  const handleSendInterview = useCallback(() => {
    if (selectedCandidates.length === 0) {
      toast.error(t('job:toasts.no_candidate_selected'));
      return;
    }

    if (job?.requiresReview) {
      toast.error(t('job:toasts.job_incomplete'));
      return;
    }

    // Guard action with limit check
    guardInterviewAction(() => {
      setShowSendInterviewDialog(true);
    })();
  }, [selectedCandidates.length, job?.requiresReview, guardInterviewAction]);

  // Confirm and send interview handler
  const handleConfirmSendInterview = useCallback(async () => {
    if (selectedCandidates.length === 0) {
      return;
    }

    // Close dialog
    setShowSendInterviewDialog(false);

    try {
      const byId = new Map((candidates ?? []).map((item) => [item.id, item]));
      const toSend = selectedCandidates.filter((id) => {
        const candidate = byId.get(id);
        return !candidate || !isInterviewInvitationAlreadySent(candidate);
      });

      if (toSend.length === 0) {
        applyActionFeedback(
          t,
          'job:toasts',
          feedbackForSendInterview({ skipped: true })
        );
        setSelectedCandidates([]);
        return;
      }

      await selectCandidatesMutation.mutateAsync({
        candidateIds: toSend,
        examConfig
      });

      applyActionFeedback(
        t,
        'job:toasts',
        feedbackForSendInterview({ willNotify: interviewingWillNotify })
      );
      setSelectedCandidates([]);

      // Invalidate queries to refresh the UI
      if (jobId) {
        invalidateJobQueries(jobId);
      }
    } catch (error) {
      console.error('Error selecting candidates for interview:', error);
      applyActionFeedback(
        t,
        'job:toasts',
        feedbackForSendInterview({
          httpError: feedbackErrorMessage(error),
        })
      );
    }
  }, [
    selectedCandidates,
    candidates,
    examConfig,
    jobId,
    invalidateJobQueries,
    selectCandidatesMutation,
    interviewingWillNotify,
    t,
  ]);
  // Unified dialog handlers
  const handleOpenDialog = (type: 'delete' | 'archive', job: JobOffer, origin?: string) => {
    setDialogType(type);
    setSelectedJobForAction(job);
    setDialogOrigin(origin);
    setShowConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
    setDialogType(null);
    setSelectedJobForAction(null);
    setDialogOrigin(undefined);
  };

  const handleConfirmAction = () => {
    if (!selectedJobForAction || !dialogType) return;

    const isDelete = dialogType === 'delete';
    const actionHandler = isDelete ? handleDeleteJob : handleArchiveJob;

    actionHandler(selectedJobForAction);
    handleCloseDialog();
  };


  const handleDeleteJob = (job: JobOffer) => {
    const loadingToastId = toast.loading(t('job:toasts.deleting', { title: job.title }));

    deleteJobMutation.mutate(job.id, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success(t('job:toasts.deleted', { title: job.title }));
        navigate('/jobs');
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(t('job:toasts.delete_failed', { error: error.message }));
      }
    });
  };

  const handleArchiveJob = (job: JobOffer) => {
    const loadingToastId = toast.loading(t('job:toasts.archiving', { title: job.title }));

    archiveJobMutation.mutate(job.id, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success(t('job:toasts.archived'));
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(t('job:toasts.archive_failed', { error: error.message }));
      }
    });
  };


  const handleCloseStepperModal = useCallback(() => {
    setShowStepperModal(false);

    if (!job?.requiresReview) {
      return;
    }

    setIsRefineDismissed(true);

    if (dismissedStorageKey && typeof window !== 'undefined') {
      const submissionStarted = window.sessionStorage.getItem(dismissedStorageKey) === '1';
      if (submissionStarted) {
        toast.info(t('job:toasts.exam_config_background'));
      }
    }
  }, [job?.requiresReview, dismissedStorageKey, t]);

  const handleBackToDashboard = useCallback(() => {
    navigate('/jobs');
  }, [navigate]);

  // Download job description handler
  const handleDownloadJobDescription = useCallback(async () => {
    if (!job) return;
    await downloadJobDescriptionForRecruiter(job);
  }, [job, downloadJobDescriptionForRecruiter]);

  // Generated from the job's current fields (reflects edits + workspace logo).
  const handleDownloadJobOfferPdf = useCallback(async () => {
    if (!job) return;
    await downloadJobOfferForCandidate(job);
  }, [job, downloadJobOfferForCandidate]);

  const hasUploadedJobDescription = hasReadyUploadedJobDescription(job?.documents);

  // Prefer updatedAt; fall back to createdAt. Skip invalid dates (no "Invalid date" copy).
  const jobOfferPdfLastUpdatedHint = useMemo(() => {
    const date = resolveJobOfferLastUpdatedDate(job?.updatedAt, job?.createdAt);
    if (!date) return null;
    return t('job:header.download_job_offer_pdf_hint', { date });
  }, [job?.updatedAt, job?.createdAt, t]);

  const jobDownloadMenuProps = useMemo(
    () => ({
      onDownloadPdf: handleDownloadJobOfferPdf,
      onDownloadDescription: handleDownloadJobDescription,
      hasUploadedJobDescription,
      pdfLastUpdatedHint: jobOfferPdfLastUpdatedHint,
      pdfTitle: t('job:header.download_job_offer_pdf_title'),
      pdfAriaLabel: t('job:header.download_job_offer_pdf'),
      descriptionTitle: t('job:header.download_description_title'),
      descriptionHint: t('job:header.download_description_hint'),
      descriptionAriaLabel: t('job:header.download_description'),
    }),
    [
      handleDownloadJobOfferPdf,
      handleDownloadJobDescription,
      hasUploadedJobDescription,
      jobOfferPdfLastUpdatedHint,
      t,
    ],
  );

  const handleToggleView = useCallback(() => {
    setIsDashboardOpen((prev) => !prev);
  }, []);

  const handleCandidatesViewChange = useCallback(
    (value: CandidatesPipelineView) => {
      if (value === 'dashboard') {
        setIsDashboardOpen(true);
        return;
      }
      setIsDashboardOpen(false);
      setSavedCandidatesView(value);
    },
    [setSavedCandidatesView]
  );

  // Always land on the saved candidate list view when the Candidates tab is (re)opened —
  // the performance-charts view should not persist across tab navigation.
  useEffect(() => {
    setIsDashboardOpen(false);
  }, [activeTab]);

  const handleStageChange = useCallback((stageId: string) => {
    setSelectedStageId(stageId);
    setSelectedCandidates([]);
    setIsDashboardOpen(false); // Leave the charts view when the stage changes
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, []);


  // Reset refreshing state when candidates finish loading
  useEffect(() => {
    if (isRefreshing && !isCandidatesLoading) {
      const timer = setTimeout(() => {
        setIsRefreshing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isCandidatesLoading, isRefreshing]);


  const selectedCandidate = candidates?.find(candidate => candidate.id === selectedCandidateId);

  if (isLoading) {
    return <Loading />;
  }

  if (!isDemoMode && error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center px-4 py-16">
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          <div className="p-2 mb-3 bg-red/20 rounded-lg">
            <AlertCircle className="w-8 h-8 text-red" />
          </div>
          <h1 className="text-2xl font-bold text-dark-invirtus mb-4 font-heading">{t('job:errors.loading_title')}</h1>
          <p className="text-gray-600 mb-6 font-body">{t('job:errors.loading_message', { error: error.message })}</p>
          <Button
            label={t('job:errors.return_to_jobs')}
            type="contained"
            intent="primary"
            onClick={() => navigate('/jobs')}
          />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <h1 className="text-2xl font-bold text-dark-invirtus mb-4 font-heading">{t('job:errors.not_found_title')}</h1>
            <p className="text-gray-600 mb-6 font-body">{t('job:errors.not_found_message')}</p>
            <Button
              label={t('job:errors.return_to_jobs')}
              type="contained"
              intent="primary"
              onClick={() => navigate('/jobs')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col bg-background-gray overflow-hidden">

        {/* Create Job Stepper (also handles refine flow) */}
        {showStepperModal && jobId && (
          <CreateJobModal
            onClose={handleCloseStepperModal}
            jobId={jobId}
            initialPhase="review"
            initialFile={stepperDraft.file}
            initialDescription={stepperDraft.description}
            onDraftChange={setStepperDraft}
          />
        )}

        {showEditJobModal && job && (
          <EditJobDetailsModal job={job} onClose={() => setShowEditJobModal(false)} />
        )}

        {/* Unified Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showConfirmDialog}
          onClose={handleCloseDialog}
          title={dialogType === 'delete' ? t('job:dialogs.confirm_delete_title') : t('job:dialogs.confirm_archive_title')}
          message={
            dialogType === 'delete'
              ? t('job:dialogs.confirm_delete_message')
              : t('job:dialogs.confirm_archive_message')
          }
          gtm={dialogType === 'delete' ? 'DELETE_JOB' : 'ARCHIVE_JOB'}
          origin={dialogOrigin}
          buttons={[
            {
              label: t('common:actions.cancel'),
              onClick: handleCloseDialog,
              type: 'outlined',
              intent: 'secondary',
              disabled: deleteJobMutation.isPending || archiveJobMutation.isPending,
            },
            {
              label:
                deleteJobMutation.isPending || archiveJobMutation.isPending
                  ? dialogType === 'delete'
                    ? t('common:status.deleting')
                    : t('common:status.archiving')
                  : dialogType === 'delete'
                    ? t('common:actions.delete')
                    : t('common:actions.archive'),
              onClick: handleConfirmAction,
              type: 'contained',
              intent: dialogType === 'delete' ? 'danger' : 'secondary',
              disabled: deleteJobMutation.isPending || archiveJobMutation.isPending,
            },
          ]}
        />

        {/* Unsaved exam changes — tab switches and route navigations */}
        <ConfirmationDialog
          isOpen={routeBlocker.state === 'blocked'}
          onClose={() => routeBlocker.reset?.()}
          title={t('job:dialogs.unsaved_title')}
          message={t('job:dialogs.unsaved_message')}
          buttons={[
            {
              label: t('job:dialogs.unsaved_discard'),
              onClick: () => { setExamDirty(false); routeBlocker.proceed?.(); },
              type: 'outlined',
              intent: 'secondary',
            },
            {
              label: t('job:dialogs.unsaved_save_leave'),
              onClick: () => examEditorRef.current?.save(),
              intent: 'secondary',
            },
          ]}
        />

        {/* Send Interview Confirmation Dialog */}
        <SendInterviewConfirmationDialog
          open={showSendInterviewDialog}
          candidateIds={selectedCandidates}
          candidates={candidates || []}
          onClose={() => setShowSendInterviewDialog(false)}
          onConfirm={handleConfirmSendInterview}
          isPending={selectCandidatesMutation.isPending}
          origin="action_bar"
          willNotify={interviewingWillNotify}
        />

        {/* Add CV Modal */}
        {showAddCvModal && job && (
          <AddCvModal
            job={job}
            onClose={handleCloseAddCvModal}
            onUploadComplete={handleCvUploadComplete}
          />
        )}

        {/* Exam Config Modal - replaced with full-page route */}
        {/* {showExamChooserModal && job && (
          <ExamConfigModal
            job={job}
            onClose={handleCloseExamChooserModal}
            onConfigChange={handleExamConfigChange}
          />
        )} */}


        {/* Uploaded CVs Modal */}
        {showUploadedCvsModal && (
          <UploadedCvsModal
            job={job}
            isOpen={showUploadedCvsModal}
            onClose={handleCloseUploadedCvsModal}
          />
        )}

        {/* Browse Resume Bank Modal */}
        {showBrowseResumeBankModal && jobId && (
          <BrowseResumeBankModal
            jobId={jobId}
            onClose={handleCloseBrowseResumeBankModal}
            onSuccess={handleBrowseResumeBankSuccess}
          />
        )}

        {/* Header — title + status + primary action + ⋯ overflow + tab strip */}
        <header className="shrink-0 bg-light">
          <div className="px-4 lg:px-6 pt-3 pb-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1 flex items-center gap-3">
                <Button
                  type="icon"
                  intent="muted"
                  size="sm"
                  onClick={handleBackToDashboard}
                  aria-label={t('job:header.back')}
                  title={t('job:header.back')}
                  label={t('job:header.back')}
                  icon={<ChevronLeft/>}
                />
                <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight truncate">
                    {job?.title || t('job:header.job_fallback')}
                  </h4>
                  {job?.source === 'MANATAL' && <ManatalBadge size="sm" />}
                </div>
                {/* Business unit tags — JOB-01 association-via-tag (mock/fixture-driven display) */}
                {(job as (typeof job & { businessUnits?: string[] }))?.businessUnits && (job as (typeof job & { businessUnits?: string[] })).businessUnits!.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {(job as (typeof job & { businessUnits?: string[] })).businessUnits!.map((bu) => (
                      <Tag key={bu} intent="primary" variant="subtle">
                        {bu}
                      </Tag>
                    ))}
                  </div>
                )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {activeTab === 'candidates' && (
                  <Button
                    type={selectedCandidates.length > 0 ? 'contained' : 'outlined'}
                    intent="secondary"
                    label={t('job:candidates.send_interview')}
                    icon={<Send />}
                    labelClassName="hidden sm:inline"
                    onClick={handleSendInterview}
                    disabled={
                      selectedCandidates.length === 0 ||
                      selectCandidatesMutation.isPending ||
                      job?.requiresReview
                    }
                  />
                )}
                {/* Add candidate — the four sourcing methods now live on the
                    Sourcing tab, so this jumps straight there instead of a menu. */}
                <Button
                  type="contained"
                  intent="secondary"
                  label={t('job:header.add_cv')}
                  icon={<FilePlus2/>}
                  labelClassName="hidden sm:inline"
                  onClick={() => setActiveTab('sourcing')}
                />
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      type="icon"
                      intent="muted"
                      aria-label={t('job:header.more_actions')}
                      icon={<MoreVertical />}
                    />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" className="min-w-72">
                    <DropdownMenu.Item onClick={() => setShowEditJobModal(true)} disabled={!job}>
                      <Pencil className="w-3.5 h-3.5 text-dark-invirtus/70" />
                      <span>{t('job:header.edit_job')}</span>
                    </DropdownMenu.Item>
                    <JobDownloadMenuItems {...jobDownloadMenuProps} />
                    <DropdownMenu.Item onClick={() => job && handleOpenDialog('archive', job)}>
                      <Archive className="w-3.5 h-3.5 text-dark-invirtus/70" />
                      <span>{t('job:header.archive_job')}</span>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      variant="destructive"
                      onClick={() => job && handleOpenDialog('delete', job, 'action_bar')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{t('job:header.delete_job')}</span>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </div>
            </div>
          </div>
          {/* Tab strip — border on the page chrome; Tabs atom owns underline triggers. */}
          <div className="px-4 lg:px-6 overflow-x-auto border-b border-light-lila/40">
            <Tabs
              aria-label={t('job:tabs.aria_label')}
              orientation="horizontal"
              variant="line"
              size="md"
              color="primary"
              value={activeTab}
              onValueChange={(id) => setActiveTab(id as JobDetailTab)}
              items={[
                {
                  id: 'overview',
                  label: t('job:tabs.overview'),
                  icon: <Briefcase />,
                },
                {
                  id: 'candidates',
                  label: t('job:tabs.candidates'),
                  icon: <Users />,
                  count: Array.isArray(candidates) ? candidates.length : undefined,
                },
                {
                  id: 'exam',
                  label: t('job:tabs.exam'),
                  icon: <Settings />,
                },
                {
                  id: 'sourcing',
                  label: t('job:tabs.sourcing'),
                  icon: <FileText />,
                },
                {
                  id: 'stages',
                  label: t('job:tabs.stages'),
                  icon: <Layers />,
                },
              ]}
            />
          </div>
        </header>

        {/* Tab body */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === 'overview' && (
            <div className="h-full overflow-y-auto bg-background-gray">
              <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
                {(() => {
                  // Normalize: each item is a string (treat as must_have) OR a {name, importance} object.
                  type NormSkill = { name: string; mustHave: boolean };
                  const normalize = (arr?: (string | { name: string; importance: 'must_have' | 'nice_to_have' | 'uncertain' })[]): NormSkill[] =>
                    (arr || [])
                      .map((s) =>
                        typeof s === 'string'
                          ? { name: s.trim(), mustHave: true }
                          : { name: s.name?.trim(), mustHave: s.importance !== 'nice_to_have' },
                      )
                      .filter((s) => s.name);

                  // Humanize enum-shaped values (FULL_TIME → "Full time"). Leaves
                  // free-text values (locations, sentence-case strings) untouched.
                  const humanize = (v?: string | null): string => {
                    const raw = (v ?? '').trim();
                    if (!raw) return '';
                    if (/_/.test(raw) || raw === raw.toUpperCase()) {
                      return raw
                        .replace(/[_-]+/g, ' ')
                        .toLowerCase()
                        .replace(/^./, (c) => c.toUpperCase());
                    }
                    return raw;
                  };

                  // Skills & requirements is a technical-skill section only: it splits
                  // skills into must have / nice to have. Business domains
                  // (`businessKnowledge`) and `softSkills` are deliberately excluded.
                  const allSkills: NormSkill[] = [
                    ...normalize(job?.skills),
                    ...normalize(job?.codingLanguages),
                    ...normalize(job?.frameworksLibraries),
                  ];
                  const mustHave = allSkills.filter((s) => s.mustHave);
                  const niceToHave = allSkills.filter((s) => !s.mustHave);

                  const facts: { icon: React.ReactNode; label: string; value: string }[] = [];
                  if (job?.location)
                    facts.push({ icon: <MapPin className="h-3.5 w-3.5" />, label: t('job:overview.fact_location'), value: job.location });
                  if (job?.remotePolicy)
                    facts.push({ icon: <Wifi className="h-3.5 w-3.5" />, label: t('job:overview.fact_work_mode'), value: humanize(job.remotePolicy) });
                  if (job?.contractType)
                    facts.push({ icon: <ScrollText className="h-3.5 w-3.5" />, label: t('job:overview.fact_contract'), value: humanize(job.contractType) });
                  if (job?.yearsOfExperience !== undefined && job?.yearsOfExperience !== null)
                    facts.push({
                      icon: <Clock className="h-3.5 w-3.5" />,
                      label: t('job:overview.fact_experience'),
                      value: t('job:overview.fact_experience_year', { count: job.yearsOfExperience }),
                    });
                  if (job?.requiredDegree)
                    facts.push({ icon: <GraduationCap className="h-3.5 w-3.5" />, label: t('job:overview.fact_degree'), value: humanize(job.requiredDegree) });
                  if (job?.companySector)
                    facts.push({ icon: <Building2 className="h-3.5 w-3.5" />, label: t('job:overview.fact_industry'), value: job.companySector });
                  if (job?.createdAt)
                    facts.push({
                      icon: <Calendar className="h-3.5 w-3.5" />,
                      label: t('job:overview.fact_posted'),
                      value: formatDate(job.createdAt),
                    });

                  const hasMainContent =
                    !!job?.description ||
                    mustHave.length > 0 ||
                    niceToHave.length > 0 ||
                    !!job?.companyDescription;

                  if (!hasMainContent && facts.length === 0) {
                    return (
                      <div className="rounded-xl border border-light-lila/40 bg-light p-10 text-center max-w-md mx-auto">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dark-invirtus-soft mb-3">
                          <Briefcase className="h-5 w-5 text-dark-invirtus/60" />
                        </div>
                        <h6 className="text-sm font-semibold text-dark-invirtus font-heading">
                          {t('job:overview.not_analyzed_title')}
                        </h6>
                        <p className="mt-1 text-xs text-dark-invirtus/60 font-body">
                          {t('job:overview.not_analyzed_hint')}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-5">
                      {/* Company header */}
                      {(job?.companyName || job?.companySector) && (
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-soft/50 shrink-0">
                            <Building2 className="h-6 w-6 text-dark-invirtus" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-base font-semibold text-dark-invirtus font-heading truncate">
                              {job?.companyName || 'Company'}
                            </div>
                            {job?.companySector && (
                              <div className="text-xs text-dark-invirtus/60 font-body truncate">
                                {job.companySector}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Two columns — narrative left, job stats right */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                        <div className="lg:col-span-2 space-y-5">
                          {/* Role details — horizontal strip, sits above the narrative (left column width). */}
                          {facts.length > 0 && (
                            <section className="rounded-xl border border-light-lila/40 bg-light overflow-hidden">
                              <header className="h-11 px-5 border-b border-light-lila/30 flex items-center justify-between gap-2">
                                <h6 className="text-xs font-semibold uppercase tracking-[0.07em] text-dark-invirtus/55 font-body">
                                  {t('job:overview.role_details')}
                                </h6>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="icon"
                                    intent="muted"
                                    size="sm"
                                    onClick={() => setShowEditJobModal(true)}
                                    aria-label={t('job:header.edit_job')}
                                    title={t('job:header.edit_job')}
                                    icon={<Pencil />}
                                  />
                                  <DropdownMenu>
                                    <DropdownMenu.Trigger asChild>
                                      <Button
                                        type="icon"
                                        intent="muted"
                                        size="sm"
                                        aria-label={t('job:header.download_options')}
                                        title={t('job:header.download_options')}
                                        icon={<Download />}
                                      />
                                    </DropdownMenu.Trigger>
                                    <DropdownMenu.Content align="end" className="min-w-72">
                                      <JobDownloadMenuItems {...jobDownloadMenuProps} />
                                    </DropdownMenu.Content>
                                  </DropdownMenu>
                                </div>
                              </header>
                              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
                                {facts.map((f) => (
                                  <div key={f.label} className="flex items-center gap-2.5 min-w-0">
                                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-dark-invirtus-soft text-dark-invirtus/60">
                                      {f.icon}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-[11px] uppercase tracking-wide text-dark-invirtus/45 font-body">
                                        {f.label}
                                      </div>
                                      <div className="truncate text-sm font-medium text-dark-invirtus font-body" title={f.value}>
                                        {f.value}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}
                          {job?.description && (
                            <OverviewCard title={t('job:overview.about_role')}>
                              <p className="text-sm text-dark-invirtus/85 font-body leading-relaxed whitespace-pre-wrap">
                                {job.description}
                              </p>
                            </OverviewCard>
                          )}
                          {(mustHave.length > 0 || niceToHave.length > 0) && (
                            <OverviewCard title={t('job:overview.skills_requirements')}>
                              <div className="space-y-4">
                                {mustHave.length > 0 && (
                                  <OverviewSkillGroup label={t('job:overview.must_have')} skills={mustHave} pillClass="bg-purple-soft/60 text-dark-invirtus" />
                                )}
                                {niceToHave.length > 0 && (
                                  <OverviewSkillGroup label={t('job:overview.nice_to_have')} skills={niceToHave} pillClass="bg-dark-invirtus-soft text-dark-invirtus/80" />
                                )}
                              </div>
                            </OverviewCard>
                          )}
                          {job?.companyDescription && (
                            <OverviewCard title={t('job:overview.about_company')}>
                              <p className="text-sm text-dark-invirtus/85 font-body leading-relaxed whitespace-pre-wrap">
                                {job.companyDescription}
                              </p>
                            </OverviewCard>
                          )}
                          {job && (
                            <OverviewCard title={t('job:weighting.title')}>
                              <div className="space-y-4">
                                <p className="text-sm text-dark-invirtus/70 font-body">
                                  {t('job:weighting.description')}
                                </p>

                                <MatchingPrioritySelector
                                  value={matchingPriorities}
                                  onChange={setMatchingPriorities}
                                  labels={{
                                    yoe: t('job:weighting.fields.yoe'),
                                    skills_match: t('job:weighting.fields.skills_match'),
                                    degree: t('job:weighting.fields.degree'),
                                    sector_experience: t('job:weighting.fields.sector_experience'),
                                  }}
                                  priorityLabels={{
                                    low: t('job:weighting.priority_levels.low'),
                                    medium: t('job:weighting.priority_levels.medium'),
                                    high: t('job:weighting.priority_levels.high'),
                                    top: t('job:weighting.priority_levels.top'),
                                  }}
                                />

                                <div className="flex flex-wrap justify-end gap-2">
                                  <Button
                                    type="outlined"
                                    intent="secondary"
                                    size="md"
                                    className="min-w-40 whitespace-nowrap"
                                    label={t('job:weighting.reset_default')}
                                    disabled={updateJobOfferMutation.isPending}
                                    onClick={() => setMatchingPriorities({ ...DEFAULT_MATCHING_PRIORITIES })}
                                  />
                                  <Button
                                    type="contained"
                                    intent="secondary"
                                    size="md"
                                    className="min-w-40 whitespace-nowrap"
                                    label={t('job:weighting.save')}
                                    disabled={updateJobOfferMutation.isPending || !hasWeightChanges}
                                    onClick={() => {
                                      updateJobOfferMutation.mutate(
                                        { matching_priorities: matchingPriorities },
                                        {
                                          onSuccess: () => {
                                            setSavedMatchingPriorities({ ...matchingPriorities });
                                            toast.success(t('job:weighting.saved'));
                                          },
                                          onError: (error: Error) => {
                                            toast.error(
                                              t('job:weighting.save_error', { error: error.message }),
                                            );
                                          },
                                        },
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            </OverviewCard>
                          )}
                        </div>

                        <JobStatsPanel job={job as JobOffer} candidates={candidates || []} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          {activeTab === 'candidates' && (
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0 overflow-hidden bg-light">
                {candidatesView === 'dashboard' ? (
                  <div className="h-full flex flex-col min-h-0">
                    <CandidatesToolbar
                      features={{
                        statusTabs: false,
                        search: false,
                        filters: false,
                        sort: false,
                        bulkActions: false,
                        viewSwitcher: true,
                      }}
                      view={candidatesView}
                      onViewChange={handleCandidatesViewChange}
                    />
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <CandidatesDashboard
                        candidates={candidates || []}
                        job={job}
                        techScores={techScores}
                        softSkillsScores={softSkillsScores}
                        overallScores={fitScores}
                        onStatusUpdate={handleCandidatesSelected}
                        onClose={handleToggleView}
                      />
                    </div>
                  </div>
                ) : candidatesView === 'kanban' ? (
                  <CandidateKanban
                    candidates={candidates || []}
                    job={job as JobOffer}
                    examConfig={examConfig}
                    requiresReview={job?.requiresReview ?? false}
                    isJobClosed={job?.status === JobStatus.CLOSED}
                    preselectCandidateId={preselectCandidateId}
                    searchTerm={searchTerm}
                    debouncedSearchTerm={debouncedSearchTerm}
                    onSearchTermChange={setSearchTerm}
                    onDebouncedSearchChange={setDebouncedSearchTerm}
                    onStatusUpdate={handleRefreshCandidates}
                    onCandidateClick={handleCandidateClick}
                    fitScores={fitScores}
                    techScores={techScores}
                    softSkillsScores={softSkillsScores}
                    cheatingFlags={cheatingFlags}
                    candidatesView={candidatesView}
                    onCandidatesViewChange={handleCandidatesViewChange}
                    onSelectedCandidatesChange={handleSelectedCandidatesChange}
                  />
                ) : (
                  <CandidatesTable
                    candidates={candidates || []}
                    isLoading={isCandidatesLoading}
                    activeStageId={activeStageId}
                    preselectCandidateId={preselectCandidateId}
                    onAddCv={handleOpenAddCvModal}
                    onSelectedCandidatesChange={handleSelectedCandidatesChange}
                    isJobClosed={job?.status === JobStatus.CLOSED}
                    requiresReview={job?.requiresReview ?? false}
                    examConfig={examConfig}
                    fitScores={fitScores}
                    techScores={techScores}
                    softSkillsScores={softSkillsScores}
                    cheatingFlags={cheatingFlags}
                    onCandidateClick={handleCandidateClick}
                    onRefresh={handleRefreshCandidates}
                    job={job as JobOffer}
                    onVisibleCandidatesChange={setVisibleCandidates}
                    searchTerm={searchTerm}
                    debouncedSearchTerm={debouncedSearchTerm}
                    onSearchTermChange={setSearchTerm}
                    onDebouncedSearchChange={setDebouncedSearchTerm}
                    candidatesView={candidatesView}
                    onCandidatesViewChange={handleCandidatesViewChange}
                    onStageChange={handleStageChange}
                  />
                )}
              </div>
            </div>
          )}
          {activeTab === 'exam' && (
            <div className="h-full overflow-hidden bg-light">
              <ExamConfigEditor
                ref={examEditorRef}
                jobId={jobId || ''}
                onBack={() => setActiveTab('candidates')}
                onSaveSuccess={() => {
                  if (routeBlocker.state === 'blocked') {
                    routeBlocker.proceed?.();
                  }
                }}
                onDirtyChange={setExamDirty}
              />
            </div>
          )}
          {activeTab === 'sourcing' && job && (
            <SourcingTab
              job={job}
              onOpenManualUpload={handleOpenAddCvModal}
              onOpenTalentPool={() => setShowAddFromPoolModal(true)}
              onOpenAgent={() => setShowSourceAgentModal(true)}
            />
          )}
          {activeTab === 'stages' && jobId && (
            <StageManagementTab jobId={jobId} />
          )}
        </div>


        {/* Candidate Modal */}
        <CandidateSidebar
          candidate={selectedCandidate}
          isOpen={isCandidateSidebarOpen}
          onClose={() => {
            setIsCandidateSidebarOpen(false);
            setSelectedCandidateId(undefined);
          }}
          job={job}
          examConfig={examConfig}
          requiresReview={job?.requiresReview ?? false}
          onStatusUpdate={handleCandidatesSelected}
          allCandidates={metaCvNavigationCandidates}
          onNavigateToCandidate={(candidateId) => {
            setSelectedCandidateId(candidateId);
          }}
        />

        {/* Add from pool panel — right Sheet (talent pool, plan 01-04, D-08) */}
        {jobId && (
          <AddFromPoolModal
            open={showAddFromPoolModal}
            jobId={jobId}
            onClose={() => setShowAddFromPoolModal(false)}
            onAdded={() => {
              // Refresh the candidate list so the newly assigned talent appears
              if (jobId) handleRefreshCandidates();
            }}
          />
        )}

        {/* Source with Agent — right Sheet hosting the LangGraph sourcing agent */}
        {jobId && (
          <SourceWithAgentSheet
            open={showSourceAgentModal}
            jobId={jobId}
            onClose={() => setShowSourceAgentModal(false)}
            onAdded={() => {
              if (jobId) handleRefreshCandidates();
            }}
          />
        )}
      </div>
    </>
  );
};

export default JobDetailsPage;
